import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert } from '@/components/ui/alert'
import { Select } from '@/components/ui/select'
import { Card } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { paymentsApi } from '@/lib/api/payments'
import { vendorsApi } from '@/lib/api/vendors'
import type { Vendor } from '@portal/types'
import { amountToWords } from '@/lib/amountToWords'
import { CreatePaymentSchema } from '@portal/schemas'
import type { z } from 'zod'

type FormValues = z.infer<typeof CreatePaymentSchema>

const PAYMENT_MODES = [
  { value: 'NEFT', label: 'NEFT' },
  { value: 'RTGS', label: 'RTGS (min ₹2,00,000)' },
  { value: 'IMPS', label: 'IMPS' },
  { value: 'FT', label: 'FT (within ICICI)' },
  { value: 'UPI', label: 'UPI (max ₹1,00,000)' },
]

interface Props {
  onSuccess?: () => void
}

export default function PaymentForm({ onSuccess }: Props) {
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [vendorSearch, setVendorSearch] = useState('')
  const [mode, setMode] = useState<'vendor' | 'manual'>('vendor')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(CreatePaymentSchema),
    mode: 'onChange',
    defaultValues: { paymentMode: 'NEFT' },
  })

  const amountInRupees = watch('amountInRupees')
  const selectedVendorId = watch('vendorId')
  const paymentMode = watch('paymentMode')
  const isUPI = paymentMode === 'UPI'

  // Load vendors on search change
  useEffect(() => {
    vendorsApi.list(vendorSearch || undefined).then((res) => {
      if (res.success && res.data) setVendors(res.data)
    }).catch(() => {})
  }, [vendorSearch])

  // When vendor is selected, prefill payee name and UPI VPA if available
  const handleVendorChange = (vendorId: string) => {
    setValue('vendorId', vendorId, { shouldValidate: true })
    const vendor = vendors.find((v) => v.id === vendorId)
    if (vendor) {
      setValue('payeeName', vendor.name, { shouldValidate: true })
      if (isUPI && vendor.upiVpa) {
        setValue('upiVpa', vendor.upiVpa, { shouldValidate: true })
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (values: any) => {
    setPendingValues(values as FormValues)
    setConfirmOpen(true)
  }

  const confirmSubmit = async () => {
    if (!pendingValues) return
    setSubmitting(true)
    setError('')
    try {
      const createRes = await paymentsApi.create(pendingValues)
      if (!createRes.success || !createRes.data) throw new Error(createRes.error ?? 'Failed to create payment')

      const submitRes = await paymentsApi.submit(createRes.data.id)
      if (!submitRes.success) throw new Error(submitRes.error ?? 'Failed to submit for approval')

      setSuccess(true)
      setConfirmOpen(false)
      reset()
      onSuccess?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setSubmitting(false)
    }
  }

  if (success) {
    return (
      <Card className="p-8 text-center">
        <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-400" />
        <h3 className="text-lg font-semibold">Payment Submitted</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Your payment has been submitted for approval.
        </p>
        <Button className="mt-6" onClick={() => setSuccess(false)}>
          Create Another
        </Button>
      </Card>
    )
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Mode selector */}
        <div className="flex gap-2">
          <Button
            type="button"
            variant={mode === 'vendor' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setMode('vendor'); setValue('vendorId', undefined) }}
          >
            Select Vendor
          </Button>
          <Button
            type="button"
            variant={mode === 'manual' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setMode('manual'); setValue('vendorId', undefined) }}
          >
            Manual Entry
          </Button>
        </div>

        {/* Vendor combobox */}
        {mode === 'vendor' && (
          <div className="space-y-2">
            <Label>Vendor</Label>
            <Input
              placeholder="Search vendors..."
              value={vendorSearch}
              onChange={(e) => setVendorSearch(e.target.value)}
            />
            {vendors.length > 0 && (
              <div className="rounded-md border bg-background shadow-sm">
                {vendors.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    className={`flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-accent ${selectedVendorId === v.id ? 'bg-accent' : ''}`}
                    onClick={() => handleVendorChange(v.id)}
                  >
                    <span className="font-medium">{v.name}</span>
                    <span className="text-xs text-muted-foreground">{v.bankIfsc} · {v.bankAccountMasked}</span>
                  </button>
                ))}
              </div>
            )}
            {errors.vendorId && <p className="text-xs text-destructive">{errors.vendorId.message}</p>}
          </div>
        )}

        {/* Manual fields */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="payeeName">Payee Name</Label>
            <Input id="payeeName" placeholder="As per bank records" {...register('payeeName')} />
            {errors.payeeName && <p className="text-xs text-destructive">{errors.payeeName.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="paymentMode">Payment Mode</Label>
            <Select id="paymentMode" {...register('paymentMode')}>
              {PAYMENT_MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </Select>
            {errors.paymentMode && <p className="text-xs text-destructive">{errors.paymentMode.message}</p>}
          </div>

          {isUPI ? (
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="upiVpa">UPI VPA</Label>
              <Input
                id="upiVpa"
                placeholder="e.g. vendor@icici"
                {...register('upiVpa')}
              />
              <p className="text-xs text-muted-foreground">Send directly to any UPI address — no account number needed</p>
              {errors.upiVpa && <p className="text-xs text-destructive">{errors.upiVpa.message}</p>}
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="creditAccount">Beneficiary Account</Label>
                <Input id="creditAccount" placeholder="9–18 digit account number" {...register('creditAccount')} />
                {errors.creditAccount && <p className="text-xs text-destructive">{errors.creditAccount.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="creditIfsc">IFSC Code</Label>
                <Input
                  id="creditIfsc"
                  placeholder="e.g. ICIC0000011"
                  className="uppercase"
                  {...register('creditIfsc', { setValueAs: (v) => v?.toUpperCase() })}
                />
                {errors.creditIfsc && <p className="text-xs text-destructive">{errors.creditIfsc.message}</p>}
              </div>
            </>
          )}

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="amountInRupees">Amount (₹)</Label>
            <Input
              id="amountInRupees"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              {...register('amountInRupees', { valueAsNumber: true })}
            />
            {amountInRupees > 0 && (
              <p className="text-xs text-muted-foreground italic">{amountToWords(amountInRupees)}</p>
            )}
            {errors.amountInRupees && <p className="text-xs text-destructive">{errors.amountInRupees.message}</p>}
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="remarks">Remarks (optional)</Label>
            <Input id="remarks" placeholder="Max 32 characters" maxLength={32} {...register('remarks')} />
            {errors.remarks && <p className="text-xs text-destructive">{errors.remarks.message}</p>}
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <span className="ml-2 text-sm">{error}</span>
          </Alert>
        )}

        <Button type="submit" disabled={!isValid} className="w-full">
          Review & Submit
        </Button>
      </form>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Payment</DialogTitle>
          </DialogHeader>

          {pendingValues && (
            <div className="space-y-3 text-sm">
              <Row label="Payee" value={pendingValues.payeeName} />
              <Row label="Mode" value={pendingValues.paymentMode} />
              {pendingValues.paymentMode === 'UPI' ? (
                <Row label="UPI VPA" value={pendingValues.upiVpa ?? ''} />
              ) : (
                <>
                  <Row label="Account" value={`XXXX${(pendingValues.creditAccount ?? '').slice(-4)}`} />
                  <Row label="IFSC" value={pendingValues.creditIfsc ?? ''} />
                </>
              )}
              <Row
                label="Amount"
                value={`₹${pendingValues.amountInRupees.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                bold
              />
              <p className="text-xs italic text-muted-foreground">{amountToWords(pendingValues.amountInRupees)}</p>
              {pendingValues.remarks && <Row label="Remarks" value={pendingValues.remarks} />}
            </div>
          )}

          <p className="mt-4 text-xs text-muted-foreground">
            This payment will be submitted for approval. Once approved it will be processed via ICICI Bank.
          </p>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={confirmSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirm & Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={bold ? 'font-semibold' : ''}>{value}</span>
    </div>
  )
}
